import { useState, useCallback, useMemo, useEffect } from "react";
import { DoorSize } from "../../../entities-generated/common/door-size";
import { IDoorSizeOption } from "../../../entities-generated/common/door-size-option";
import { StormDoorColor } from "../../../entities-generated/common/storm-door-color";
import { StormDoorHandleType } from "../../../entities-generated/common/storm-door-handle-type";
import { IStormDoorHandleTypeOption } from "../../../entities-generated/common/storm-door-handle-type-option";
import { ILowesStormDoorEditingModel, LowesStormDoorEditingModel } from "../../../entities-generated/models/lowes-storm-door/lowes-storm-door-editing-model";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace, isNullOrWhitespace } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { LowesStormDoorEvents } from "../events";
import { IPublishDoorFormModel, IPublishDoorValueModel, IPublishDoorModelChange } from "./publish-door-iface";
import { IStormDoorColorOption } from "../../../entities-generated/common/storm-door-color-option";
import { ILowesProductTexture } from "../../../entities-generated/common/lowes/lowes-product-texture";
import { IPublishLowesStormDoorRequest, PublishLowesStormDoorRequest } from "../../../entities-generated/models/lowes-storm-door/publish-lowes-storm-door-request";


// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<ILowesStormDoorEditingModel, string> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:vendorUrl/immutable/string%%
export const vendorUrl = new (class vendorUrlService extends ImmutableFormValueService<ILowesStormDoorEditingModel, string> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.value = data.vendorUrl;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.vendorUrl = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'vendorUrl' // id
    ,'' // description
);
// %%END:vendorUrl/immutable/string%%

// %%BEGIN:description/validated/string/string%%
export const description = new (class descriptionService extends ValidatedFormValueService<ILowesStormDoorEditingModel, string, string> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.description));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.description = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Provide description');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'description' // id
    ,'Description' // description
);
// %%END:description/validated/string/string%%

// %%BEGIN:imageUrls/validated/string[]/string[]%%
export const imageUrls = new (class imageUrlsService extends ValidatedFormValueService<ILowesStormDoorEditingModel, string[], string[]> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string[]>(data.imageUrls));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.imageUrls = this.value.value;
    }

    public getDefaultValue(): BwjInput<string[]> {
        return new BwjInput<string[]>([]);
    }

    public getInputValue(): BwjInput<string[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        return this.value.value;
    }

    public validate(value: string[]): Promise<BwjInput<string[]>> {
        const result = this.getDefaultValue();
        result.value = value;
        return Promise.resolve(result);
    }

    public onUpdate(value: string[]): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'imageUrls' // id
    ,'Primary images' // description
);
// %%END:imageUrls/validated/string[]/string[]%%

// %%BEGIN:sellingPoints/validated/string/string[]%%
export const sellingPoints = new (class sellingPointsService extends ValidatedFormValueService<ILowesStormDoorEditingModel, string, string[]> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.sellingPoints.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.sellingPoints = this.value.value.split('\n');
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        if(isNullOrWhitespace(value)) { return []; }

        const values = value.split('\n');
        var outValue = where(values, v => isNotNullOrWhitespace(v));
        return outValue;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getDefaultValue();
        result.value = value;
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }
})(
    'sellingPoints' // id
    ,'Sell points' // description
);
// %%END:sellingPoints/validated/string/string[]%%

// %%BEGIN:doorSize/validated/IDoorSizeOption | null/DoorSize%%
export const doorSize = new (class doorSizeService extends ValidatedFormValueService<ILowesStormDoorEditingModel, IDoorSizeOption | null, DoorSize> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.options = data.doorSizes;
        this.setInputValue(new BwjInput<IDoorSizeOption | null>(null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.doorSizes = this.options;
    }

    public getDefaultValue(): BwjInput<IDoorSizeOption | null> {
        return new BwjInput<IDoorSizeOption | null>(null);
    }

    public getInputValue(): BwjInput<IDoorSizeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorSizeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): DoorSize {
        const value = this.value.value;
        return value?.id ?? DoorSize.Indeterminate;
    }

    public validate(value: IDoorSizeOption | null): Promise<BwjInput<IDoorSizeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a door size');
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorSizeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IDoorSizeOption[] = [];
})(
    'doorSize' // id
    ,'Door size' // description
);
// %%END:doorSize/validated/IDoorSizeOption | null/DoorSize%%

// %%BEGIN:validHandlesets/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%
export const validHandlesets = new (class validHandlesetsService extends ValidatedFormValueService<ILowesStormDoorEditingModel, IStormDoorHandleTypeOption | null, StormDoorHandleType> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.options = data.doorHandleTypes;
        this.setInputValue(new BwjInput<IStormDoorHandleTypeOption | null>(null));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.doorHandleTypes = this.options;
    }

    public getDefaultValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return new BwjInput<IStormDoorHandleTypeOption | null>(null);
    }

    public getInputValue(): BwjInput<IStormDoorHandleTypeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IStormDoorHandleTypeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorHandleType {
        const value = this.value.value;
        return value?.id ?? StormDoorHandleType.Indeterminate;
    }

    public validate(value: IStormDoorHandleTypeOption | null): Promise<BwjInput<IStormDoorHandleTypeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Provide the handleset that can be used with this door');
        return Promise.resolve(result);
    }

    public onUpdate(value: IStormDoorHandleTypeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorHandleTypeOption[] = [];
})(
    'validHandlesets' // id
    ,'Valid handleset' // description
);
// %%END:validHandlesets/validated/IStormDoorHandleTypeOption | null/StormDoorHandleType%%

// %%BEGIN:variantColorFamilies/validated/string/StormDoorColor[]%%
export const variantColorFamilies = new (class variantColorFamiliesService extends ValidatedFormValueService<ILowesStormDoorEditingModel, string, StormDoorColor[]> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.options = data.colorFamilies;
        this.variants = data.colorVariants;
        this.setInputValue(new BwjInput<string>(''));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.colorFamilies = this.options;
        data.colorVariants = this.variants;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorColor[] {
        const value = this.value.value;
        const optValues = value.split('\n').map(t => firstOrDefault(this.options, o => StormDoorColor[o.id] === t));
        const values = where(optValues, v => !!v).map(x => x!.id);
        return values;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getDefaultValue();
        result.value = value;

        const optValues = value.split('\n').map(t => firstOrDefault(this.options, o => StormDoorColor[o.id] === t));
        const values = where(optValues, v => !!v) as IStormDoorColorOption[];
        if((this.variants.length > 0 && values.length !== this.variants.length)
            || (this.variants.length === 0 && values.length !== 1)) {
            result.validity = BwjValidityState.Invalid;
            result.message = 'Provide a color family value for each variant';
        }
        
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorColorOption[] = [];
    private variants: ILowesProductTexture[] = [];
})(
    'variantColorFamilies' // id
    ,'Variant color families' // description
);
// %%END:variantColorFamilies/validated/string/StormDoorColor[]%%

// %%BEGIN:productReviewSummary/validated/string/string%%
export const productReviewSummary = new (class productReviewSummaryService extends ValidatedFormValueService<ILowesStormDoorEditingModel, string, string> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.productReviewSummary));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {
        data.productReviewSummary = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getDefaultValue();
        result.value = value;
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'productReviewSummary' // id
    ,'Product review summary' // description
);
// %%END:productReviewSummary/validated/string/string%%

// %%BEGIN:includesHandle/mutable/boolean/boolean%%
export const includesHandle = new (class includesHandleService extends MutableFormValueService<ILowesStormDoorEditingModel, boolean, boolean> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.value = false;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'includesHandle' // id
    ,'Handle included with door' // description
);
// %%END:includesHandle/mutable/boolean/boolean%%

// %%BEGIN:cutdownPossible/mutable/boolean/boolean%%
export const cutdownPossible = new (class cutdownPossibleService extends MutableFormValueService<ILowesStormDoorEditingModel, boolean, boolean> {
    public initialize(data: ILowesStormDoorEditingModel): void {
        this.value = false;
    }

    public applyToDataModel(data: ILowesStormDoorEditingModel): void {}

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'cutdownPossible' // id
    ,'Door can be cut down' // description
);
// %%END:cutdownPossible/mutable/boolean/boolean%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IPublishDoorFormModel => {
    return {
       id: id.getInputValue(),
       vendorUrl: vendorUrl.getInputValue(),
       description: description.getInputValue(),
       imageUrls: imageUrls.getInputValue(),
       sellingPoints: sellingPoints.getInputValue(),
       doorSize: doorSize.getInputValue(),
       validHandlesets: validHandlesets.getInputValue(),
       variantColorFamilies: variantColorFamilies.getInputValue(),
       productReviewSummary: productReviewSummary.getInputValue(),
       includesHandle: includesHandle.getInputValue(),
       cutdownPossible: cutdownPossible.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IPublishDoorValueModel => {
    return {
       description: description.getOutputValue(),
       imageUrls: imageUrls.getOutputValue(),
       sellingPoints: sellingPoints.getOutputValue(),
       doorSize: doorSize.getOutputValue(),
       validHandlesets: validHandlesets.getOutputValue(),
       variantColorFamilies: variantColorFamilies.getOutputValue(),
       productReviewSummary: productReviewSummary.getOutputValue(),
       includesHandle: includesHandle.getOutputValue(),
       cutdownPossible: cutdownPossible.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        description.getInputValue(),
        imageUrls.getInputValue(),
        sellingPoints.getInputValue(),
        doorSize.getInputValue(),
        validHandlesets.getInputValue(),
        variantColorFamilies.getInputValue(),
        productReviewSummary.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IPublishDoorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(LowesStormDoorEvents.PublishFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IPublishDoorFormModel>> => {
    const promises = [
        description.formValidateAndUpdate(),
        imageUrls.formValidateAndUpdate(),
        sellingPoints.formValidateAndUpdate(),
        doorSize.formValidateAndUpdate(),
        validHandlesets.formValidateAndUpdate(),
        variantColorFamilies.formValidateAndUpdate(),
        productReviewSummary.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IPublishDoorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IPublishDoorValueModel, dataModel: ILowesStormDoorEditingModel } = {
    dataModel: new LowesStormDoorEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: ILowesStormDoorEditingModel) => {
    const dataModel = data || new LowesStormDoorEditingModel();

    id.initialize(dataModel);
    vendorUrl.initialize(dataModel);
    description.initialize(dataModel);
    imageUrls.initialize(dataModel);
    sellingPoints.initialize(dataModel);
    doorSize.initialize(dataModel);
    validHandlesets.initialize(dataModel);
    variantColorFamilies.initialize(dataModel);
    productReviewSummary.initialize(dataModel);
    includesHandle.initialize(dataModel);
    cutdownPossible.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IPublishDoorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
description.setChangePublisher(broadcastChange);
imageUrls.setChangePublisher(broadcastChange);
sellingPoints.setChangePublisher(broadcastChange);
doorSize.setChangePublisher(broadcastChange);
validHandlesets.setChangePublisher(broadcastChange);
variantColorFamilies.setChangePublisher(broadcastChange);
productReviewSummary.setChangePublisher(broadcastChange);
includesHandle.setChangePublisher(broadcastChange);
cutdownPossible.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new LowesStormDoorEditingModel();

    id.applyToDataModel(dataModel);
    vendorUrl.applyToDataModel(dataModel);
    description.applyToDataModel(dataModel);
    imageUrls.applyToDataModel(dataModel);
    sellingPoints.applyToDataModel(dataModel);
    doorSize.applyToDataModel(dataModel);
    validHandlesets.applyToDataModel(dataModel);
    variantColorFamilies.applyToDataModel(dataModel);
    productReviewSummary.applyToDataModel(dataModel);
    includesHandle.applyToDataModel(dataModel);
    cutdownPossible.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IPublishDoorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], LowesStormDoorEvents.PublishFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IPublishDoorFormModel => {
    const [getter, setter] = useState<IPublishDoorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IPublishDoorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IPublishDoorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('PublishDoorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('PublishDoorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toPublishRequest = () => {
    const v = toValueModel();
    const familyId = v.description
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, ' ')
        .replace(/\s+/g, '-');

    const request: IPublishLowesStormDoorRequest = {
        vendor: 'Lowes',
        description: v.description,
        imageUrls: v.imageUrls,
        sellingPoints: v.sellingPoints,
        isRejected: false,
        familyId: familyId,
        doorSize: v.doorSize,
        validHandlesets: v.validHandlesets,
        includesHandle: v.includesHandle,
        cutdownPossible: v.cutdownPossible,
        variantColorFamilies: v.variantColorFamilies,
        productReviewSummary: v.productReviewSummary
    };
    return request;
}

export const toPublishRejectionRequest = () => {
    const request = new PublishLowesStormDoorRequest();
    request.isRejected = true;
    return request;
}

// ##END:DEVELOPERCODE##
